import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Plus, Volume2, VolumeX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import { useCart } from '@/context/CartContext';
import { useProducts } from '@/lib/shopify';
import { formatPrice } from '@/data/products';

const FILMS = [
  { id: 'define-aura', title: 'Define Aura', detail: 'The first frame is never quiet.', collection: 'Hoodies', video: '/campaign-v2/define-aura.mp4' },
  { id: 'find-your-fire', title: 'Find your fire', detail: 'Graphic weight. Easy movement.', collection: 'T-Shirts', video: '/campaign-v2/find-your-fire.mp4' },
  { id: 'wide-leg-study', title: 'Wide leg study', detail: 'Built to move through the city.', collection: 'BOTTOMS', video: '/campaign-v2/wide-leg-study.mp4' },
  { id: 'open-your-mind', title: 'Open your mind', detail: 'Your layer. Your signal.', collection: 'Hoodies', video: '/campaign-v2/open-your-mind.mp4' },
  { id: 'fallen-angel', title: 'Fallen angel', detail: 'Detail makes the whole look.', collection: 'Jackets & Coats', video: '/campaign-v2/fallen-angel.mp4' },
].map((film) => ({ ...film, webm: film.video.replace('.mp4', '.webm') }));

function useScrollDepth(reference) {
  const [depth, setDepth] = useState(0);
  useEffect(() => {
    const update = () => {
      const frame = reference.current?.getBoundingClientRect();
      if (!frame) return;
      setDepth(Math.max(0, Math.min(1, -frame.top / Math.max(1, frame.height - window.innerHeight))));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [reference]);
  return depth;
}

function useChapterPlayback(chapter) {
  const video = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const element = video.current;
    const section = chapter.current;
    if (!element || !section) return undefined;
    const beginPlayback = () => element.play().catch(() => {});
    element.muted = true;
    element.defaultMuted = true;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) beginPlayback();
      else element.pause();
    }, { threshold: 0.15 });
    observer.observe(section);
    return () => { observer.disconnect(); element.pause(); };
  }, [chapter]);

  const turnSound = useCallback(async () => {
    if (!video.current) return;
    const nextMuted = !video.current.muted;
    video.current.muted = nextMuted;
    setMuted(nextMuted);
    try { await video.current.play(); } catch { /* User can retry with the same control. */ }
  }, []);

  return { video, muted, turnSound };
}

function ChapterProduct({ film, product, add, setOpen }) {
  if (!product) return <Link to="/shop" data-testid={`v2-film-fallback-link-${film.id}`} className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-white">View live collection</Link>;
  const quickAdd = () => {
    add(product);
    toast.success(`${product.name} added to cart`);
    setOpen(true);
  };
  return <>
    <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-white/55">Live Shopify edit</p>
    <Link to={`/products/${product.id}`} data-testid={`v2-film-product-link-${film.id}`} className="mt-2 block font-syne text-lg font-bold uppercase text-white transition-colors hover:text-[#C7D1C3]">{product.name}</Link>
    <p className="mt-2 font-mono2 text-xs text-white/70">{formatPrice(product.price, product.currency)}</p>
    <button type="button" onClick={quickAdd} data-testid={`v2-film-quick-add-${film.id}`} className="mt-5 flex items-center gap-2 border border-white/35 px-4 py-3 font-mono2 text-[10px] font-bold uppercase tracking-[0.17em] text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-[#1E1E1E]"><Plus size={14} /> Quick add</button>
  </>;
}

function FilmChapter({ film, index, product, collection }) {
  const chapter = useRef(null);
  const { add, setOpen } = useCart();
  const depth = useScrollDepth(chapter);
  const { video, muted, turnSound } = useChapterPlayback(chapter);
  const transform = `perspective(1400px) translateY(${depth * -2}%) rotateY(${(depth - 0.5) * (index % 2 ? -14 : 14)}deg) rotateX(${(depth - 0.5) * -5}deg) scale(${0.9 + depth * 0.1})`;
  const collectionLink = collection ? `/shop?collection=${collection.handle}` : '/shop';
  return <section ref={chapter} className={`video-chapter video-chapter-${index % 2 ? 'right' : 'left'}`} data-testid={`v2-film-chapter-${film.id}`}>
    <div className="video-chapter-sticky">
      <div className="video-chapter-grid">
        <div className="video-chapter-copy">
          <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.28em] text-[#C7D1C3]">Film / {String(index + 1).padStart(2, '0')}</p>
          <h2 className="mt-4 font-syne text-4xl font-extrabold uppercase leading-[0.88] text-white sm:text-5xl lg:text-6xl">{film.title}</h2>
          <p className="mt-5 max-w-[260px] text-sm leading-6 text-white/65">{film.detail}</p>
          <Link to={collectionLink} data-testid={`v2-film-shop-link-${film.id}`} className="mt-8 inline-flex items-center gap-2 border-b border-[#C7D1C3] pb-2 font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] text-white">Shop {film.collection} <ArrowUpRight size={14} /></Link>
        </div>
        <div className="video-stage" style={{ transform }} data-testid={`v2-video-stage-${film.id}`}>
          <video ref={video} poster={product?.image} loop muted playsInline preload="metadata" className="video-stage-main" data-testid={`v2-video-${film.id}`}>
            <source src={film.webm} type="video/webm" />
            <source src={film.video} type="video/mp4" />
          </video>
          <div className="video-stage-depth video-stage-depth-one" aria-hidden="true" /><div className="video-stage-depth video-stage-depth-two" aria-hidden="true" />
          <button type="button" onClick={turnSound} data-testid={`v2-video-sound-${film.id}`} className="video-sound-button" aria-label={muted ? 'Turn video sound on' : 'Turn video sound off'}>{muted ? <VolumeX size={15} /> : <Volume2 size={15} />}</button>
        </div>
        <div className="video-chapter-product">
          <ChapterProduct film={film} product={product} add={add} setOpen={setOpen} />
        </div>
      </div>
      <p className="video-chapter-number" aria-hidden="true">0{index + 1}</p>
    </div>
  </section>;
}

export default function VideoExperience() {
  const { products, collections } = useProducts();
  return <main className="video-experience bg-[#101311] pt-[72px]" data-testid="video-experience-page">
    <section className="video-intro" data-testid="v2-hero">
      <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.28em] text-[#C7D1C3]">Aura Studio 98 / Version 02</p>
      <h1 className="mt-5 max-w-6xl font-syne text-5xl font-extrabold uppercase leading-[0.84] text-white sm:text-6xl lg:text-7xl">Clothes in<br /><span className="text-[#9EAC99]">motion.</span></h1>
      <p className="mt-7 max-w-sm text-sm leading-7 text-white/65">Five campaign films. One moving collection. Scroll to enter the wardrobe.</p>
      <ArrowDownRight className="mt-10 text-[#C7D1C3]" size={30} aria-hidden="true" />
    </section>
    {FILMS.map((film, index) => <FilmChapter key={film.id} film={film} index={index} product={products.find((product) => product.collections?.some((entry) => entry.title === film.collection))} collection={collections.find((entry) => entry.title === film.collection)} />)}
    <section className="video-outro" data-testid="v2-outro"><p className="font-mono2 text-[10px] uppercase tracking-[0.27em] text-[#657260]">The collection continues</p><h2 className="mt-4 font-syne text-4xl font-extrabold uppercase leading-[0.9] sm:text-5xl lg:text-6xl">Find your<br /><span className="steel-text">next frame.</span></h2><Link to="/shop" data-testid="v2-shop-all-link" className="mt-8 inline-flex items-center gap-3 bg-[#1E1E1E] px-5 py-4 font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#657260]">Shop all pieces <ArrowUpRight size={15} /></Link></section>
    <Footer />
  </main>;
}